// React
import React, { useEffect, useState, useMemo } from "react";

// Redux
import { connect } from "react-redux";
import { getChatsAndMessages } from "@Actions/chats";

// Libraries
import PropTypes from "prop-types";
import { Grid } from "semantic-ui-react";
import { isEmpty, find, orderBy, map, filter, reduce, get, includes } from "lodash";

// Components
import Screen from "@Components/Screen";
import ChatList from "./Components/ChatList";
import ChatArea from "./Components/ChatArea";
import ChatInfo from "./Components/ChatInfo";
import SearchMessages from "./Components/SearchMessages";

// Hooks
import usePanelVisibility from "@Hooks/usePanelVisibility";

// Context
import { ListRefProvider } from "./Context";

// Utils
import { getDummyMessage, isWhatsappBlocked } from "./Utils";

// Styles
import styles from "./styles.module.css";

const Monitor = ({
	dispatch,
	isHistoric = false,
	isSearchEnabled,
	chats,
	messages,
	currentChatId,
	chatsH,
	messagesH,
	currentChatHId,
	userId,
	userState,
}) => {
	const [allItems, setAllItems] = useState(null);
	const [sortInverted, setSortInverted] = useState(false);
	const [historicTodayChat, setHistoricTodayChat] = useState(null);
	const { showList, showChat, showInfo } = usePanelVisibility();

	const currentChat = useMemo(() => {
		if (historicTodayChat) {
			return (
				find(allItems, (item) => {
					return item.id === historicTodayChat.id;
				}) || historicTodayChat
			);
		}
		return find(allItems, (item) => {
			return isHistoric ? currentChatHId === item.id : currentChatId === item.id;
		});
	}, [isHistoric, currentChatId, currentChatHId, allItems, historicTodayChat]);
	const isChatDisabled =
		!!historicTodayChat ||
		isHistoric ||
		get(currentChat, "userId", null) !== userId ||
		!includes(["En Línea", "No Asigna Chat"], userState);
	const isWhatsappBlock = isWhatsappBlocked(currentChat);

	useEffect(() => {
		if (!isHistoric) {
			dispatch(getChatsAndMessages());
		}
	}, [isHistoric]);

	useEffect(() => {
		if (isHistoric) {
			setHistoricTodayChat(null);
		}
	}, [isHistoric]);

	useEffect(() => {
		const useHistoricData = isHistoric || !!historicTodayChat;
		const activeChats = useHistoricData ? chatsH : chats;
		const activeMessages = useHistoricData ? messagesH : messages;
		const allChats = map(activeChats, (chat) => {
			const allMessages = filter(activeMessages, ({ channelCid, chatId }) => {
				return useHistoricData ? chatId === chat.id : channelCid === chat.channelCid;
			});
			if (isEmpty(allMessages)) {
				return {
					...chat,
					message: getDummyMessage(chat.channelCid),
					state: "Cliente",
					stateIndex: 2,
					messages: [],
				};
			}
			const lastMsg = reduce(
				allMessages.filter((item) => {
					return !item.autogestion;
				}),
				(max, msg) => {
					return max?.timestamp > msg?.timestamp ? max : msg;
				}
			);
			let state = "";
			let stateIndex = 0;
			if (chat.newChat) {
				state = "Nuevo";
				stateIndex = 0;
			} else if (!chat.newChat && lastMsg && lastMsg.sentByAgent) {
				state = "Cliente";
				stateIndex = 2;
			} else if (!chat.newChat && lastMsg && !lastMsg.sentByAgent) {
				state = "Atender";
				stateIndex = 1;
			}
			return { ...chat, message: lastMsg, state, stateIndex, messages: allMessages };
		});
		const timestampDir = sortInverted ? "desc" : "asc";
		const ordered =
			isHistoric || !!historicTodayChat ? allChats : orderBy(allChats, ["stateIndex", "message.timestamp"], ["asc", timestampDir]);

		setAllItems(ordered);
	}, [chats, messages, chatsH, messagesH, isHistoric, sortInverted, historicTodayChat]);

	const toggleSort = () => {
		setSortInverted((prev) => {
			return !prev;
		});
	};

	return (
		<Screen hasSidebar hasHeader headerTitle={isHistoric ? "Histórico de Chat" : "Monitor"}>
			<ListRefProvider>
				<Grid className={styles.containerSidebarContext}>
					<Grid.Column
						className={styles.column}
						widescreen={4}
						largeScreen={4}
						computer={6}
						tablet={6}
						mobile={16}
						style={{
							display: showList ? undefined : "none",
						}}>
						<ChatList
							isHistoric={isHistoric}
							chats={allItems}
							currentChatId={get(currentChat, "id", 0)}
							currentChat={currentChat}
							sortInverted={sortInverted}
							toggleSort={toggleSort}
							setHistoricTodayChat={setHistoricTodayChat}
							historicTodayChat={historicTodayChat}
						/>
					</Grid.Column>
					<Grid.Column
						className={styles.column}
						widescreen={8}
						largeScreen={8}
						computer={10}
						tablet={10}
						mobile={16}
						style={{
							display: showChat ? undefined : "none",
						}}>
						<ChatArea
							isHistoric={isHistoric || !!historicTodayChat}
							currentChat={currentChat}
							isChatDisabled={isChatDisabled}
							isWhatsappBlock={isWhatsappBlock}
						/>
					</Grid.Column>

					<Grid.Column
						className={styles.column}
						widescreen={4}
						largeScreen={4}
						computer={6}
						tablet={6}
						mobile={16}
						style={{
							display: showInfo ? undefined : "none",
						}}>
						{isSearchEnabled ? (
							<SearchMessages channelCid={currentChat?.channelCid} currentChat={currentChat} />
						) : (
							<ChatInfo
								isHistoric={isHistoric || !!historicTodayChat}
								currentChat={currentChat}
								isChatDisabled={isChatDisabled}
								isWhatsappBlock={isWhatsappBlock}
							/>
						)}
					</Grid.Column>
				</Grid>
			</ListRefProvider>
		</Screen>
	);
};

Monitor.propTypes = {
	dispatch: PropTypes.func,
	isHistoric: PropTypes.bool,
	isSearchEnabled: PropTypes.bool,
	chats: PropTypes.array,
	messages: PropTypes.array,
	currentChatId: PropTypes.number,
	chatsH: PropTypes.array,
	messagesH: PropTypes.array,
	currentChatHId: PropTypes.number,
	userState: PropTypes.string,
	userId: PropTypes.number,
};

function mapStateToProps({ chats, history, user }) {
	return {
		isSearchEnabled: chats.isSearchEnabled,
		chats: chats.chats,
		messages: chats.messages,
		currentChatId: chats.currentChatId,
		chatsH: history.chats,
		messagesH: history.messages,
		currentChatHId: history.currentChatId,
		userState: user.state,
		userId: user.userId,
	};
}

export default connect(mapStateToProps)(Monitor);
